import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { mergeVisibleLessonsForStudent } from './studentLessons';

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizeLessonDoc(docSnap) {
  return {
    lessonId: docSnap.id,
    ...docSnap.data(),
  };
}

async function queryLessonsByEmail(db, email) {
  if (!email) return [];

  const normalizedEmail = normalizeEmail(email);
  const results = [];

  try {
    const normalizedSnapshot = await getDocs(
      query(collection(db, 'lessonInstances'), where('studentEmailNormalized', '==', normalizedEmail))
    );
    results.push(...(normalizedSnapshot?.docs || []).map(normalizeLessonDoc));
  } catch (error) {
    // Older documents may not carry normalized email yet.
  }

  try {
    const rawSnapshot = await getDocs(
      query(collection(db, 'lessonInstances'), where('studentEmail', '==', email))
    );
    results.push(...(rawSnapshot?.docs || []).map(normalizeLessonDoc));
  } catch (error) {
    // Keep reads safe when raw email queries are unavailable.
  }

  return results;
}

export async function findClaimableLessonsByEmail(db, currentUser = {}) {
  const userId = String(currentUser?.uid || '').trim();
  const userEmail = normalizeEmail(currentUser?.email || '');
  if (!userId || !userEmail) return [];

  const lessons = await queryLessonsByEmail(db, currentUser.email);

  return lessons.filter((lesson) => {
    const lessonStudentId = String(lesson?.studentId || '').trim();
    const lessonEmail = normalizeEmail(
      lesson?.studentEmailNormalized || lesson?.studentEmail || ''
    );

    if (lessonStudentId) return false;
    if (!lessonEmail) return false;
    return lessonEmail === userEmail;
  });
}

export async function claimPendingLessonsForUser(
  db,
  currentUser = {},
  { studentName = '' } = {}
) {
  const userId = String(currentUser?.uid || '').trim();
  const userEmail = normalizeEmail(currentUser?.email || '');
  if (!userId || !userEmail) {
    return { claimedCount: 0, claimedLessonIds: [], claimError: '' };
  }

  const claimableLessons = await findClaimableLessonsByEmail(db, currentUser);
  if (claimableLessons.length === 0) {
    return { claimedCount: 0, claimedLessonIds: [], claimError: '' };
  }

  const claimedLessonIds = [];

  for (const lesson of claimableLessons) {
    await updateDoc(doc(db, 'lessonInstances', lesson.lessonId), {
      studentId: userId,
      studentEmail: lesson.studentEmail || currentUser.email || '',
      studentEmailNormalized: userEmail,
      studentName: lesson.studentName || studentName || '',
      inviteStatus: 'claimed',
      inviteLastError: '',
      updatedAt: serverTimestamp(),
    });
    claimedLessonIds.push(lesson.lessonId);
  }

  return {
    claimedCount: claimedLessonIds.length,
    claimedLessonIds,
    claimError: '',
  };
}

export async function loadVisibleLessonsForStudent(
  db,
  currentUser = {},
  { studentName = '', claimPending = true } = {}
) {
  let claimedCount = 0;
  let claimError = '';

  if (claimPending) {
    try {
      const claimResult = await claimPendingLessonsForUser(db, currentUser, { studentName });
      claimedCount = claimResult.claimedCount;
      claimError = claimResult.claimError || '';
    } catch (error) {
      claimError = 'We found lessons for your email, but could not link them to your account yet.';
    }
  }

  const lessonResults = [];

  if (currentUser?.uid) {
    const byStudentId = await getDocs(
      query(collection(db, 'lessonInstances'), where('studentId', '==', currentUser.uid))
    );
    lessonResults.push(...(byStudentId?.docs || []).map(normalizeLessonDoc));
  }

  if (currentUser?.email) {
    lessonResults.push(...(await queryLessonsByEmail(db, currentUser.email)));
  }

  return {
    lessons: mergeVisibleLessonsForStudent(lessonResults, currentUser),
    claimedCount,
    claimError,
  };
}
